import { useState, useEffect, useMemo } from 'react';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { Download, Wand2, AlertCircle } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Field } from '@/components/ui/Input';
import { Dropzone } from '@/components/ui/Dropzone';
import { Badge } from '@/components/ui/Badge';
import { useApp } from '@/store/app';
import { saveAs } from 'file-saver';
import { toast } from 'sonner';
import { readFileAsArrayBuffer, bytesToBlob } from '@/lib/utils';
import { pdfjs } from '@/lib/pdfjs';

interface PlaceholderHit {
  /** placeholder name without braces, e.g. "name" */
  field: string;
  page: number;
  /** PDF user-space coordinates (origin at bottom-left). */
  x: number;
  y: number;
  width: number;
  height: number;
  fontSize: number;
}

interface PageRender {
  page: number;
  url: string;
  width: number; // viewport pixel width at scale 1.5
  height: number;
  pdfWidth: number; // page user-space width (points)
  pdfHeight: number;
}

const PLACEHOLDER_RE = /\{\{\s*([a-zA-Z0-9_.-]+)\s*\}\}/g;

export function TemplatePdf() {
  const { pendingFile, setPendingFile } = useApp();
  const [file, setFile] = useState<File | null>(null);
  const [pages, setPages] = useState<PageRender[]>([]);
  const [hits, setHits] = useState<PlaceholderHit[]>([]);
  const [values, setValues] = useState<Record<string, string>>({});
  const [scanning, setScanning] = useState(false);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    if (pendingFile && pendingFile.type === 'application/pdf') {
      handleFile([pendingFile]);
      setPendingFile(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleFile(files: File[]) {
    const f = files[0];
    if (!f) return;
    setFile(f);
    setHits([]);
    setValues({});
    setScanning(true);
    try {
      const buf = await readFileAsArrayBuffer(f);
      const doc = await pdfjs.getDocument({ data: buf }).promise;
      const renderedPages: PageRender[] = [];
      const placeholders: PlaceholderHit[] = [];

      for (let i = 1; i <= doc.numPages; i++) {
        const page = await doc.getPage(i);
        const view = page.getViewport({ scale: 1.5 });
        const c = document.createElement('canvas');
        c.width = view.width;
        c.height = view.height;
        const ctx = c.getContext('2d')!;
        await page.render({ canvasContext: ctx, viewport: view, canvas: c }).promise;
        const userSpace = page.getViewport({ scale: 1 });

        renderedPages.push({
          page: i,
          url: c.toDataURL('image/png'),
          width: view.width,
          height: view.height,
          pdfWidth: userSpace.width,
          pdfHeight: userSpace.height,
        });

        const textContent = await page.getTextContent();
        for (const item of textContent.items) {
          if (!('str' in item)) continue;
          const str = item.str as string;
          if (!str) continue;
          const trans = item.transform as number[];
          // Reset regex state
          PLACEHOLDER_RE.lastIndex = 0;
          let match: RegExpExecArray | null;
          while ((match = PLACEHOLDER_RE.exec(str)) !== null) {
            const fontSize = Math.abs(trans[3]) || 12;
            // Approximate character width (PDFs vary widely; this is a heuristic)
            const charW = fontSize * 0.55;
            const xOffset = match.index * charW;
            const totalLen = match[0].length;
            placeholders.push({
              field: match[1],
              page: i,
              x: trans[4] + xOffset,
              y: trans[5],
              width: totalLen * charW,
              height: fontSize * 1.1,
              fontSize,
            });
          }
        }
      }

      setPages(renderedPages);
      setHits(placeholders);

      // Initialize values map with all unique fields
      const fields = Array.from(new Set(placeholders.map((p) => p.field)));
      const initial: Record<string, string> = {};
      fields.forEach((f) => (initial[f] = ''));
      setValues(initial);

      if (placeholders.length === 0) {
        toast.warning('No {{placeholder}} fields found in this PDF');
      } else {
        toast.success(`Found ${placeholders.length} placeholder${placeholders.length === 1 ? '' : 's'}`);
      }
    } catch (e) {
      toast.error('Could not parse PDF');
    } finally {
      setScanning(false);
    }
  }

  const fields = useMemo(() => Array.from(new Set(hits.map((h) => h.field))), [hits]);

  async function buildFilled() {
    if (!file || hits.length === 0) return;
    setWorking(true);
    try {
      const buf = await readFileAsArrayBuffer(file);
      const doc = await PDFDocument.load(buf);
      const helv = await doc.embedFont(StandardFonts.Helvetica);
      const docPages = doc.getPages();

      for (const hit of hits) {
        const page = docPages[hit.page - 1];
        const value = values[hit.field] ?? '';
        // Cover the original placeholder with white
        page.drawRectangle({
          x: hit.x - 1,
          y: hit.y - hit.height * 0.2,
          width: hit.width + 4,
          height: hit.height + 2,
          color: rgb(1, 1, 1),
        });
        if (value) {
          page.drawText(value, {
            x: hit.x,
            y: hit.y,
            size: hit.fontSize,
            font: helv,
            color: rgb(0.1, 0.1, 0.12),
          });
        }
      }

      const bytes = await doc.save();
      saveAs(bytesToBlob(bytes), file.name.replace(/\.pdf$/i, '') + '-filled.pdf');
      toast.success('Filled PDF saved');
    } catch (e) {
      toast.error('Failed to fill template');
    } finally {
      setWorking(false);
    }
  }

  const ready = fields.length > 0 && fields.every((f) => values[f]?.trim().length);

  return (
    <div>
      <ToolHeader
        toolId="template-pdf"
        title="Template PDF generator"
        description="Upload a PDF with {{placeholders}}, fill values, get back a filled PDF. Layout is preserved."
        badges={
          fields.length ? (
            <Badge variant="success">
              {fields.length} field{fields.length === 1 ? '' : 's'}
            </Badge>
          ) : null
        }
      />

      {!file ? (
        <>
          <Dropzone
            accept="application/pdf"
            onFiles={handleFile}
            hint="Use {{name}}, {{date}}, {{amount}} style placeholders in your PDF"
          />
          <Card className="mt-4">
            <div className="text-xs text-fg-muted leading-relaxed">
              <strong className="text-fg">How to prepare your template:</strong> open your PDF
              source (Word, Pages, Google Docs) and add fields like{' '}
              <code className="px-1 py-0.5 bg-bg-inset border border-border rounded text-2xs">{'{{name}}'}</code>,{' '}
              <code className="px-1 py-0.5 bg-bg-inset border border-border rounded text-2xs">{'{{date}}'}</code>,{' '}
              <code className="px-1 py-0.5 bg-bg-inset border border-border rounded text-2xs">{'{{amount}}'}</code>.
              Export as PDF and drop it here. We'll find every placeholder, you fill in the values,
              and download a clean filled copy.
            </div>
          </Card>
        </>
      ) : scanning ? (
        <Card>
          <div className="py-8 text-center text-sm text-fg-subtle">Scanning for placeholders…</div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-3">
            <Card className="flex items-center justify-between">
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{file.name}</div>
                <div className="text-2xs text-fg-subtle font-mono mt-0.5">
                  {pages.length} page{pages.length === 1 ? '' : 's'} · {hits.length} placeholder{hits.length === 1 ? '' : 's'}
                </div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => { setFile(null); setPages([]); setHits([]); }}>
                Change
              </Button>
            </Card>
            {pages.map((p) => {
              const pageHits = hits.filter((h) => h.page === p.page);
              return (
                <Card key={p.page} className="!p-3">
                  <div className="text-2xs font-mono uppercase tracking-wider text-fg-subtle mb-2">
                    Page {p.page}
                  </div>
                  <div className="relative inline-block w-full" style={{ aspectRatio: `${p.width} / ${p.height}` }}>
                    <img src={p.url} alt={`page ${p.page}`} className="absolute inset-0 w-full h-full" />
                    {pageHits.map((h, i) => {
                      // Convert PDF user-space (origin bottom-left) to viewport pixels
                      const left = (h.x / p.pdfWidth) * 100;
                      const top = ((p.pdfHeight - h.y - h.height) / p.pdfHeight) * 100;
                      const width = (h.width / p.pdfWidth) * 100;
                      const height = (h.height / p.pdfHeight) * 100;
                      return (
                        <div
                          key={i}
                          className="absolute border-2 border-accent bg-accent/10 rounded-sm pointer-events-none flex items-center justify-center text-2xs font-mono font-bold text-accent"
                          style={{ left: `${left}%`, top: `${top}%`, width: `${width}%`, height: `${height}%` }}
                          title={`{{${h.field}}}`}
                        >
                          <span className="truncate px-1">{values[h.field] || `{{${h.field}}}`}</span>
                        </div>
                      );
                    })}
                  </div>
                </Card>
              );
            })}
          </div>

          <Card className="space-y-3">
            <div className="text-xs font-medium text-fg-muted">Fill values</div>
            {fields.length === 0 ? (
              <div className="flex items-start gap-2 text-xs text-warning">
                <AlertCircle size={12} className="mt-0.5" />
                <div>
                  No placeholders found. Make sure your template uses{' '}
                  <code className="font-mono">{`{{field}}`}</code> syntax.
                </div>
              </div>
            ) : (
              fields.map((f) => (
                <Field key={f} label={f}>
                  <Input
                    value={values[f] ?? ''}
                    onChange={(e) => setValues((v) => ({ ...v, [f]: e.target.value }))}
                    placeholder={`Value for ${f}…`}
                  />
                </Field>
              ))
            )}
            <Button
              variant="primary"
              className="w-full mt-2"
              loading={working}
              disabled={!ready || working}
              onClick={buildFilled}
            >
              <Download size={11} />
              Generate filled PDF
            </Button>
            {!ready && fields.length > 0 && (
              <div className="text-2xs text-fg-subtle">Fill every field to enable export.</div>
            )}
            <div className="text-2xs text-fg-subtle leading-relaxed border-t border-border pt-2">
              <Wand2 size={10} className="inline mr-1 text-accent" />
              Each placeholder is whited out and replaced. Layout is preserved; font matches Helvetica.
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

export default TemplatePdf;
